import { useCallback, useEffect, useState } from "react";

// Safari still names these with a prefix.
const fullscreenElement = () => document.fullscreenElement || document.webkitFullscreenElement || null;

/**
 * Put the whole page in full screen. A browser only allows this straight
 * after a click or key press, so call it from one (the way into the room
 * does). Fails quietly: the room works the same in a window.
 */
export const enterFullscreen = () => {
  if (fullscreenElement()) return;
  const el = document.documentElement;
  const request = el.requestFullscreen || el.webkitRequestFullscreen;
  if (!request) return;
  try {
    const pending = request.call(el);
    if (pending && pending.catch) pending.catch(() => {});
  } catch {
    /* not allowed here: stay in the window */
  }
};

export const exitFullscreen = () => {
  if (!fullscreenElement()) return;
  const exit = document.exitFullscreen || document.webkitExitFullscreen;
  try {
    const pending = exit && exit.call(document);
    if (pending && pending.catch) pending.catch(() => {});
  } catch {
    /* already out */
  }
};

/**
 * Whether the page is in full screen, kept in step with the browser — Esc
 * leaves full screen without asking us — and the way in and out.
 */
export function useFullscreen() {
  const [isFullscreen, setIsFullscreen] = useState(() => Boolean(fullscreenElement()));
  const supported = Boolean(document.documentElement.requestFullscreen || document.documentElement.webkitRequestFullscreen);

  useEffect(() => {
    const sync = () => setIsFullscreen(Boolean(fullscreenElement()));
    document.addEventListener("fullscreenchange", sync);
    document.addEventListener("webkitfullscreenchange", sync);
    return () => {
      document.removeEventListener("fullscreenchange", sync);
      document.removeEventListener("webkitfullscreenchange", sync);
    };
  }, []);

  const toggle = useCallback(() => (fullscreenElement() ? exitFullscreen() : enterFullscreen()), []);

  return { isFullscreen, supported, enter: enterFullscreen, exit: exitFullscreen, toggle };
}
